import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Star } from 'lucide-react';
import { safeAnchorHref } from '../../../lib/link';
import { cn } from '../../../lib/utils';
import { specraFocusRing } from '../ensigns/focus-ring';
import { SpecraText } from '../ensigns/typography';
/** One program navigation destination with active and collapsed states. */ export function SpecraNavigationItemView({ collapsed = false, item, renderLink }) {
    const sharedProps = {
        'aria-current': item.active ? 'page' : undefined,
        'aria-label': collapsed ? item.label : undefined,
        'data-item-id': item.id,
        'data-testid': item.testId,
        className: cn('flex h-8 w-full cursor-pointer items-center gap-2.5 rounded-md px-2 text-left transition-colors', item.active ? 'bg-accent font-medium text-foreground' : 'text-muted-foreground hover:bg-accent/70 hover:text-foreground', item.indented && !collapsed && 'relative pl-7', item.indented && !collapsed && "before:absolute before:inset-y-0 before:left-3 before:w-px before:bg-muted-foreground/30 before:content-['']", collapsed && 'justify-center px-0', specraFocusRing),
        title: collapsed ? item.label : undefined
    };
    const pinLabel = item.pinned ? 'Unpin from top' : 'Pin to top';
    const togglePin = (event)=>{
        event.preventDefault();
        event.stopPropagation();
        item.onTogglePin?.();
    };
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            item.icon ? /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                className: "flex shrink-0 items-center justify-center opacity-70 [&_svg]:size-3.5",
                children: item.icon
            }) : null,
            !collapsed ? /*#__PURE__*/ _jsx(SpecraText, {
                className: "min-w-0 flex-1 truncate",
                size: "caption",
                children: item.label
            }) : null,
            !collapsed && item.pinnable ? // biome-ignore lint/a11y/useSemanticElements: the pin toggle renders inside the nav item's own <a>/<button> (Component, below), so a real <button> here would be invalid nesting — it stays a role="button" span with aria-pressed, tabIndex, and Enter/Space handlers.
            /*#__PURE__*/ _jsx("span", {
                "aria-label": pinLabel,
                "aria-pressed": item.pinned ?? false,
                className: cn('flex shrink-0 items-center rounded-sm p-0.5 transition-opacity [&_svg]:size-3', item.pinned ? 'opacity-70 hover:opacity-100' : 'opacity-20 hover:opacity-100 focus-visible:opacity-100', specraFocusRing),
                "data-testid": item.pinTestId,
                onClick: togglePin,
                onKeyDown: (event)=>{
                    if (event.key === 'Enter' || event.key === ' ') togglePin(event);
                },
                role: "button",
                tabIndex: 0,
                title: pinLabel,
                children: /*#__PURE__*/ _jsx(Star, {
                    "aria-hidden": "true",
                    fill: item.pinned ? 'currentColor' : 'none'
                })
            }) : null
        ]
    });
    if (item.href && renderLink) {
        return renderLink({
            ...sharedProps,
            children: content,
            href: item.href,
            onClick: item.onClick
        });
    }
    const fallbackHref = item.href ? safeAnchorHref(item.href) : undefined;
    const Component = fallbackHref ? 'a' : item.onClick ? 'button' : 'span';
    const interactiveProps = fallbackHref ? {
        href: fallbackHref,
        onClick: item.onClick
    } : item.onClick ? {
        onClick: item.onClick,
        type: 'button'
    } : {};
    return /*#__PURE__*/ _jsx(Component, {
        ...sharedProps,
        ...interactiveProps,
        children: content
    });
}
